import buttonStyles from '../Button/Button.scss';
import styles from './OrderSummary.scss';

import cx from 'classnames';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import useDeepCompareEffect from 'use-deep-compare-effect';

import { ContentType, InternalLink, LinkVariant } from 'src/api/contentful/contentTypes.cloverdotcom';
import { Button } from 'src/components/Button/Button';
import { useCart } from 'src/components/Cart/UseCart';
import { HardwarePaymentPlans } from 'src/components/OrderSummary/HardwarePaymentPlans/HardwarePaymentPlans';
import { PromoSummary } from 'src/components/OrderSummary/PromoSummary/PromoSummary';
import { SummarySection } from 'src/components/OrderSummary/SummarySection/SummarySection';
import { TextV2 } from 'src/components/TextV2/TextV2';
import { useDCS } from 'src/hooks/useDCS';
import { FlatEntry } from 'src/services/cms/flattenContentful';
import { CONNECT_TO_SALES_PATH } from '../App/Routes/Routes';
import { InternalLinkV2 } from '../ContentLinkV2/InternalLinkV2';
import { DueToday } from './DueToday/DueToday';
import { TaxSection } from './TaxSection/TaxSection';
import { Installments, Plans } from './types';

interface OrderSummaryProps {
  isContinueButtonDisabled?: boolean;
  continueButtonClickHandler?: React.MouseEventHandler<HTMLButtonElement>;
  backButtonVisible?: boolean;
}

export const OrderSummary: React.FC<OrderSummaryProps> = ({
  isContinueButtonDisabled,
  continueButtonClickHandler,
  backButtonVisible = true,
}) => {
  const { t } = useTranslation();
  const { cart, checkout } = useCart();
  const history = useHistory();
  const [userMustContactSales, setUserMustContactSales] = useState<boolean>(false);
  const { productsPricings, bundlesPricings } = useDCS({
    bundleIds:  [cart.bundle?.id],
    productIds: cart.products.map(p => p.id),
  });

  useDeepCompareEffect(() => {
    const userMustContactSales =
      bundlesPricings.some(b => b && !b.isSelfServicePurchaseEnabled) ||
      productsPricings.some(p => p && !p.isSelfServicePurchaseEnabled);

    setUserMustContactSales(userMustContactSales);
  }, [cart, productsPricings, bundlesPricings]);

  const plans: Plans = [
    {
      term: Installments.P,
      price: checkout.subtotal,
    },
    ...checkout.installmentPlans.map(plan => ({
      term: plan.id as any,
      price: plan.cost,
    })),
  ];

  return (
    <section>
      <TextV2
        tagName="h2"
        variant="h4"
        className={styles.title}
        value={t('order-summary.title')}
      />
      <div className={styles.wrapper}>
        <HardwarePaymentPlans plans={plans} />

        {cart.softwarePlan && (
          <SummarySection
            heading={t('order-summary.monthly-plan')}
            subheading={cart.softwarePlan.name}
            price={t('order-summary.price', { price: cart.softwarePlan.monthly })}
            description={t('order-summary.fee-waived-30-days')}
            data-test="software-plan"
          />
        )}

        <TaxSection />

        <PromoSummary />

        <DueToday />

        <div className={styles.action}>
          {userMustContactSales ? (
            <InternalLinkV2
              className={cx(buttonStyles.button, buttonStyles.buttonVariantPrimary)}
              entry={{
                contentType: ContentType.INTERNAL_LINK,
                page: { slug: CONNECT_TO_SALES_PATH },
                title: t('contact-sales'),
              } as FlatEntry<InternalLink>}
            />
          ) : (
            <Button
              variant={LinkVariant.PRIMARY}
              isFullWidth
              className={styles.actionBtn}
              disabled={isContinueButtonDisabled}
              onClick={continueButtonClickHandler}
              data-test="continue-button"
            >
              {t('continue')}
            </Button>
          )}
          {backButtonVisible && (
            <Button
              variant={LinkVariant.SECONDARY}
              isFullWidth
              className={styles.actionBtn}
              onClick={history.goBack}
              data-test="back-button"
            >
              {t('back')}
            </Button>
          )}
        </div>
      </div>
    </section>
  );
};

OrderSummary.displayName = 'OrderSummary';
